import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import { Button as SetlistButton, Spinner } from "@opendate-app/setlist";
import { toastError } from "../../../shared/toastHelper";
import { formatDateOnly } from "../../../shared/formattingUtils";
import "../../ConfirmShow/styles/makeRecurringModalStyles.css";
import "../styles/seriesCreateStyles.css";

// Use the shared formatter, which guards a bare YYYY-MM-DD against being parsed
// as UTC midnight (which renders a day early west of UTC).
const formatConfirmDate = (confirm) =>
  confirm.start_time || confirm.start_date
    ? formatDateOnly(confirm.start_time || confirm.start_date)
    : "";

const appendUnseen = (existing, rows) => {
  const seen = new Set(existing.map((confirm) => confirm.id));
  return [...existing, ...rows.filter((confirm) => !seen.has(confirm.id))];
};

export const ConfirmPickerList = ({
  open,
  teamId,
  excludeSeriesId,
  multiSelect,
  selected,
  onToggle,
}) => {
  const [query, setQuery] = useState("");
  const [pageRequest, setPageRequest] = useState({ page: 1 });
  const [loadedPage, setLoadedPage] = useState(0);
  const [confirms, setConfirms] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  const pendingFocusRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (pendingFocusRef.current === null) return;
    const index = pendingFocusRef.current;
    pendingFocusRef.current = null;
    const list = listRef.current;
    if (!list) return;
    const loadMoreButton = loadMoreRef.current?.querySelector("button");
    const focused = document.activeElement;
    const userMovedFocus = focused !== null && focused !== document.body && focused !== loadMoreButton;
    if (userMovedFocus) return;
    const rows = list.querySelectorAll(".confirm-picker-row");
    (rows[index] || loadMoreButton || rows[rows.length - 1])?.focus({ preventScroll: true });
  }, [loading]);

  const startSearch = (value) => {
    pendingFocusRef.current = null;
    setQuery(value);
    setPageRequest({ page: 1 });
    setHasMore(false);
  };

  useEffect(() => {
    if (!open) return;
    startSearch("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // Set in cleanup so a request in flight when the modal closes/unmounts (or a
    // new keystroke supersedes it) can't setState/toast on a dead component.
    let ignore = false;
    const { page } = pageRequest;
    const handle = setTimeout(() => {
      // Strobe guard: only show the spinner once the debounce actually fires, so
      // each keystroke doesn't swap the list out from under the cursor.
      setLoading(true);
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (excludeSeriesId) params.exclude_series_id = excludeSeriesId;
      if (page > 1) params.page = page;
      axios
        .get(`/teams/${teamId}/event_series/confirms`, { params })
        .then(({ data }) => {
          if (ignore) return;
          const rows = Array.isArray(data?.confirms) ? data.confirms : [];
          setConfirms((previous) => (page > 1 ? appendUnseen(previous, rows) : rows));
          setHasMore(Boolean(data?.has_more));
          setLoadedPage(page);
        })
        .catch(() => {
          if (ignore) return;
          if (page === 1) {
            setConfirms([]);
            setHasMore(false);
          }
          toastError("Could not load shows. Please try again.");
        })
        .finally(() => {
          if (!ignore) setLoading(false);
        });
    }, page > 1 ? 0 : 200);
    return () => {
      ignore = true;
      clearTimeout(handle);
    };
  }, [open, teamId, excludeSeriesId, query, pageRequest]);

  const loadMore = () => {
    if (loading) return;
    pendingFocusRef.current = confirms.length;
    setPageRequest({ page: loadedPage + 1 });
  };

  return (
    <>
      <div className="confirm-picker-search">
        <input
          type="text"
          className="confirm-picker-search-input"
          placeholder="Search events by title..."
          value={query}
          onChange={(e) => startSearch(e.target.value)}
          data-testid="confirm-picker-search"
        />
      </div>

      <div className="confirm-picker-list" ref={listRef} data-testid="confirm-picker-list">
        {loading && pageRequest.page === 1 ? (
          <div className="confirm-picker-loading">
            <Spinner size="sm" label="Loading shows..." />
          </div>
        ) : confirms.length === 0 ? (
          <div className="confirm-picker-empty">No shows found.</div>
        ) : (
          <>
            {confirms.map((confirm) => {
              const isSelected = selected.has(confirm.id);
              return (
                <button
                  key={confirm.id}
                  type="button"
                  className={`confirm-picker-row ${isSelected ? "confirm-picker-row-selected" : ""}`}
                  onClick={() => onToggle(confirm)}
                  data-testid="confirm-picker-row"
                >
                  <input
                    type={multiSelect ? "checkbox" : "radio"}
                    className="confirm-picker-checkbox"
                    checked={isSelected}
                    readOnly
                    tabIndex={-1}
                  />
                  <span className="confirm-picker-row-main">
                    <span className="confirm-picker-row-title">
                      {confirm.title}
                      {!confirm.published && <span className="confirm-picker-row-draft">Draft</span>}
                    </span>
                    <span className="d-block confirm-picker-row-meta">
                      {formatConfirmDate(confirm)}
                      {confirm.venue_name ? ` · ${confirm.venue_name}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
            {hasMore && (
              <div className="text-center py-2" ref={loadMoreRef}>
                <SetlistButton
                  variant="link-color"
                  size="sm"
                  onClick={loadMore}
                  disabled={loading}
                  data-testid="confirm-picker-load-more"
                >
                  {loading ? (
                    <span className="d-inline-flex align-items-center">
                      <Spinner size={16} color="currentColor" aria-hidden="true" className="mr-2" />
                      Loading...
                    </span>
                  ) : (
                    "Load more shows"
                  )}
                </SetlistButton>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

ConfirmPickerList.propTypes = {
  open: PropTypes.bool.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  excludeSeriesId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  multiSelect: PropTypes.bool,
  selected: PropTypes.instanceOf(Map).isRequired,
  onToggle: PropTypes.func.isRequired,
};

ConfirmPickerList.defaultProps = {
  excludeSeriesId: undefined,
  multiSelect: false,
};

const ConfirmPicker = ({
  open,
  onClose,
  teamId,
  excludeSeriesId,
  multiSelect,
  onConfirm,
  title,
  confirmLabel,
}) => {
  const [selected, setSelected] = useState(new Map());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Map());
    setSubmitting(false);
  }, [open]);

  const toggle = (confirm) => {
    setSelected((prev) => {
      const next = multiSelect ? new Map(prev) : new Map();
      if (prev.has(confirm.id) && (multiSelect || prev.size === 1)) {
        next.delete(confirm.id);
      } else {
        next.set(confirm.id, confirm);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (submitting || selected.size === 0) return;
    const values = [...selected.values()];
    setSubmitting(true);
    onConfirm(multiSelect ? values : values[0]);
  };

  return (
    <Popup open={open} className="make-recurring-popup" closeOnDocumentClick={!submitting} onClose={onClose}>
      <div className="popup-modal-container">
        <div className="row m-0 make-recurring-header">
          <div className="col d-flex py-3 px-4">
            <h3 className="mb-0 align-self-center make-recurring-title">
              <strong>{title}</strong>
            </h3>
          </div>
          <div className="col-xs-auto d-flex align-items-center pr-4">
            <a
              href="#"
              className="text-dark make-recurring-close"
              onClick={(e) => {
                e.preventDefault();
                if (!submitting) onClose();
              }}
            >
              <i className="fal fa-times" />
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12 make-recurring-body">
            <ConfirmPickerList
              open={open}
              teamId={teamId}
              excludeSeriesId={excludeSeriesId}
              multiSelect={multiSelect}
              selected={selected}
              onToggle={toggle}
            />

            <div className="d-flex justify-content-end make-recurring-actions">
              <SetlistButton
                variant="secondary"
                className="make-recurring-btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </SetlistButton>
              <SetlistButton
                variant="primary"
                className="make-recurring-btn"
                onClick={handleConfirm}
                disabled={selected.size === 0 || submitting}
                data-testid="confirm-picker-submit"
              >
                {submitting ? (
                  <span className="d-inline-flex align-items-center">
                    <Spinner size={16} color="currentColor" aria-hidden="true" className="mr-2" />
                    Adding...
                  </span>
                ) : (
                  <strong>{confirmLabel}</strong>
                )}
              </SetlistButton>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

ConfirmPicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  excludeSeriesId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  multiSelect: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  confirmLabel: PropTypes.string,
};

ConfirmPicker.defaultProps = {
  excludeSeriesId: undefined,
  multiSelect: false,
  title: "Choose a show",
  confirmLabel: "Add",
};

export default ConfirmPicker;
